import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useBulkUpsertFieldDefinitions } from "@/hooks/use-import";
import type { CustomFieldType, CustomFieldWidget } from "@/hooks/use-import";
import type { CustomFieldDefinition, ImportEntityType } from "@adva/types";

// `reference` is intentionally excluded — those fields are only created via
// industry-pack templates (see packages/services/src/core/industry-packs.ts);
// the inline wizard is for ad-hoc CSV headers, where a typed FK doesn't make
// sense without an entity-type picker.
const FIELD_TYPES: ReadonlyArray<{ value: CustomFieldType; label: string }> = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Datetime" },
  { value: "select", label: "Select" },
  { value: "multiselect", label: "Multiselect" },
];

// ui_widget options grouped by the field_type they pair with. A superset is
// allowed per the schema, but the wizard only offers the pairings that make
// operational sense for a CSV-driven workflow.
const WIDGETS_BY_TYPE: Record<
  CustomFieldType,
  ReadonlyArray<{ value: CustomFieldWidget; label: string }>
> = {
  text: [
    { value: "text", label: "Text" },
    { value: "textarea", label: "Textarea" },
    { value: "url", label: "URL" },
    { value: "phone", label: "Phone" },
    { value: "email", label: "Email" },
  ],
  number: [
    { value: "number", label: "Number" },
    { value: "currency", label: "Currency" },
  ],
  boolean: [
    { value: "checkbox", label: "Checkbox" },
    { value: "toggle", label: "Toggle" },
  ],
  date: [{ value: "date", label: "Date picker" }],
  datetime: [{ value: "datetime", label: "Datetime picker" }],
  select: [
    { value: "select", label: "Dropdown" },
    { value: "radio", label: "Radio" },
  ],
  multiselect: [
    { value: "multiselect", label: "Multiselect" },
    { value: "checkbox_group", label: "Checkboxes" },
  ],
  // ADV-910: reference is intentionally not selectable in the wizard (see
  // FIELD_TYPES above). The entry exists to keep the Record key-complete; the
  // dropdown is built from FIELD_TYPES so users can't land here.
  reference: [{ value: "text", label: "Reference (ID)" }],
};

/** Convert a CSV header into a valid field_key: lowercase snake_case. */
function headerToFieldKey(header: string): string {
  return header
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^([0-9])/, "f_$1");
}

export function InlineCreateField({
  entityType,
  sourceHeader,
  onCreated,
  onCancel,
  onError,
}: {
  entityType: ImportEntityType;
  sourceHeader: string;
  onCreated: (definition: CustomFieldDefinition) => void;
  onCancel: () => void;
  onError: (message: string) => void;
}) {
  const [label, setLabel] = useState(sourceHeader);
  const [fieldKey, setFieldKey] = useState(headerToFieldKey(sourceHeader));
  const [fieldKeyEdited, setFieldKeyEdited] = useState(false);
  const [fieldType, setFieldType] = useState<CustomFieldType>("text");
  const [uiWidget, setUiWidget] = useState<CustomFieldWidget>("text");

  const createMutation = useBulkUpsertFieldDefinitions();

  const widgets = WIDGETS_BY_TYPE[fieldType];
  const widgetIsValid = widgets.some((w) => w.value === uiWidget);

  const handleLabelChange = (next: string) => {
    setLabel(next);
    if (!fieldKeyEdited) {
      setFieldKey(headerToFieldKey(next));
    }
  };

  const handleFieldTypeChange = (next: CustomFieldType) => {
    setFieldType(next);
    const firstWidget = WIDGETS_BY_TYPE[next][0].value;
    setUiWidget(firstWidget);
  };

  const handleSubmit = async () => {
    if (!fieldKey.match(/^[a-z][a-z0-9_]*$/)) {
      onError(
        "Field key must start with a lowercase letter and contain only lowercase letters, numbers, and underscores",
      );
      return;
    }
    try {
      const result = await createMutation.mutateAsync([
        {
          entity_type: entityType,
          field_key: fieldKey,
          label: label.trim() || sourceHeader,
          field_type: fieldType,
          ui_widget: widgetIsValid ? uiWidget : widgets[0].value,
        },
      ]);
      const created = result.field_definitions.find(
        (d) => d.field_key === fieldKey && d.entity_type === entityType,
      );
      if (!created) {
        onError("Field created but could not be resolved in the response");
        return;
      }
      onCreated(created);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to create field");
    }
  };

  return (
    <div className="rounded-md border bg-muted/30 p-3 space-y-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium">
          Create custom field for{" "}
          <span className="font-mono text-xs">{sourceHeader}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Label</span>
          <input
            type="text"
            value={label}
            onChange={(e) => handleLabelChange(e.target.value)}
            className="w-full rounded-md border bg-background px-2 py-1 text-sm"
            placeholder={sourceHeader}
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Field key</span>
          <input
            type="text"
            value={fieldKey}
            onChange={(e) => {
              setFieldKey(e.target.value);
              setFieldKeyEdited(true);
            }}
            className="w-full rounded-md border bg-background px-2 py-1 text-sm font-mono"
            placeholder="snake_case_key"
            spellCheck={false}
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Field type</span>
          <select
            value={fieldType}
            onChange={(e) =>
              handleFieldTypeChange(e.target.value as CustomFieldType)
            }
            className="w-full rounded-md border bg-background px-2 py-1 text-sm"
          >
            {FIELD_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">UI widget</span>
          <select
            value={uiWidget}
            onChange={(e) => setUiWidget(e.target.value as CustomFieldWidget)}
            className="w-full rounded-md border bg-background px-2 py-1 text-sm"
          >
            {widgets.map((w) => (
              <option key={w.value} value={w.value}>
                {w.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={createMutation.isPending}
          className="rounded-md border px-3 py-1 text-xs disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={createMutation.isPending || !label.trim() || !fieldKey}
          className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
        >
          {createMutation.isPending && (
            <Loader2 className="h-3 w-3 animate-spin" />
          )}
          Create field
        </button>
      </div>
    </div>
  );
}
